import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useCountries, useCreateShipment, useShippingWorkbooks } from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  ShipmentForm,
  ShipmentFormLabWorkbook,
  ShipmentFormOrganisation,
  ShipmentFormValues,
  buildShipmentFormDefaults,
} from '../admin-view/shipment-form';

export function WorkbookShipmentView() {
  const { workbookId = '' } = useParams<{ workbookId: string }>();
  const { user, userLoading } = useUser();
  const { data, isPending: workbooksPending, error: workbooksError } = useShippingWorkbooks();
  const { data: countriesByAlpha3 } = useCountries();
  const createShipment = useCreateShipment();
  const showToast = useToastWithoutDebounce();
  const queryClient = useQueryClient();
  const [submittedValues, setSubmittedValues] = useState<ShipmentFormValues | null>(null);

  const workbook = useMemo(
    () => data?.workbooks.find(candidate => candidate.id === workbookId),
    [data?.workbooks, workbookId],
  );
  const organisationOptions = useMemo(() => {
    if (!workbook) {
      return [] as ShipmentFormOrganisation[];
    }
    return [
      {
        id: workbook.organisation.id,
        name: workbook.organisation.name,
        country: workbook.organisation.country,
        pickupAddressLine1: workbook.organisation.pickupAddressLine1,
        pickupAddressLine2: workbook.organisation.pickupAddressLine2,
        pickupAddressLine3: workbook.organisation.pickupAddressLine3,
        pickupPostalCode: workbook.organisation.pickupPostalCode,
        pickupCity: workbook.organisation.pickupCity,
        taxId: workbook.organisation.taxId,
      },
    ];
  }, [workbook]);
  const labWorkbookOptions = useMemo(() => {
    if (!workbook) {
      return [] as ShipmentFormLabWorkbook[];
    }
    return [{ id: workbook.id, name: getShippingWorkbookDisplayName(workbook) }];
  }, [workbook]);

  const defaultValues = useMemo(() => {
    if (!user || !workbook) {
      return null;
    }

    return buildShipmentFormDefaults({
      organisationId: workbook.organisation.id,
      labWorkbookId: workbook.id,
      contactName: `${user.firstName} ${user.lastName}`.trim(),
      contactEmail: user.email,
      contactTelephoneNumber: user.telephoneNumber ?? null,
      pickupCountry: workbook.organisation.country,
      pickupAddressLine1: workbook.organisation.pickupAddressLine1,
      pickupAddressLine2: workbook.organisation.pickupAddressLine2,
      pickupAddressLine3: workbook.organisation.pickupAddressLine3,
      pickupPostalCode: workbook.organisation.pickupPostalCode,
      pickupCity: workbook.organisation.pickupCity,
      taxId: workbook.organisation.taxId,
    });
  }, [user, workbook]);

  if (userLoading || workbooksPending) {
    return <LoadingView />;
  }

  if (workbooksError) {
    return (
      <Box padding={theme.spacing[6]}>
        <MaybeApiError error={workbooksError} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box padding={theme.spacing[6]}>
        <Text>Please log in to request shipping.</Text>
      </Box>
    );
  }

  if (!workbook) {
    return (
      <Box padding={theme.spacing[6]}>
        <Text color="gray.600">Lab workbook not found or not available for your account.</Text>
      </Box>
    );
  }

  if (!defaultValues) {
    return (
      <Box padding={theme.spacing[6]}>
        <Text color="gray.600">This lab workbook is not available for shipment requests.</Text>
      </Box>
    );
  }

  const canSubmit = createShipment.isSuccess || workbook.shippingRequestsAvailable > 0;

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar
        title="Request DNA Shipment Pickup"
        $extraWide={true}
        controls={
          <Text
            as={RouterLink}
            to={`/admin/sample-shipping/workbook/${workbook.id}`}
            color={theme.colors.blue800}
            fontWeight={600}
            marginLeft={theme.spacing[4]}
          >
            Back to shipment types
          </Text>
        }
      />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <Box bg="white" borderRadius={6} padding={theme.spacing[4]} boxShadow="sm">
          <HStack spacing={3} wrap="wrap">
            <Text fontWeight={600}>{getShippingWorkbookDisplayName(workbook)}</Text>
            <Badge colorScheme={workbook.shippingRequestsAvailable > 0 ? 'green' : 'red'}>
              Available: {workbook.shippingRequestsAvailable}
            </Badge>
            <Badge colorScheme="blue">Pending: {workbook.shippingRequestsPending}</Badge>
            <Badge colorScheme="purple">Spent: {workbook.shippingRequestsUsed}</Badge>
          </HStack>
          {canSubmit ? null : (
            <Text mt={3} color="red.700">
              No shipment request quota is available for this lab workbook.
            </Text>
          )}
          {createShipment.isSuccess ? (
            <Text mt={3} color="green.700">
              Shipment request submitted. The quota slot is reserved until Resistomap processes or cancels the request.
            </Text>
          ) : null}
        </Box>

        {canSubmit ? (
          <ShipmentForm
            mode="create"
            defaultValues={submittedValues ?? defaultValues}
            countriesByAlpha3={countriesByAlpha3}
            organisationOptions={organisationOptions}
            labWorkbookOptions={labWorkbookOptions}
            disableOrganisationSelect={true}
            disableLabWorkbookSelect={true}
            xlShipmentsAuthorised={workbook.xlShipmentsAuthorised}
            submitLabel="Submit shipment request"
            isSubmitting={createShipment.isPending}
            readOnly={createShipment.isSuccess}
            onSubmit={async values => {
              const createPayload = omit(values, ['status']);
              await createShipment.mutateAsync(createPayload);
              setSubmittedValues(values);
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['/shipping/workbooks'] }),
                queryClient.invalidateQueries({
                  predicate: query =>
                    typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('/shipments'),
                }),
                queryClient.invalidateQueries({
                  predicate: query =>
                    typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('/admin/shipments'),
                }),
              ]);
              showToast({
                title: 'Shipment request submitted',
                description: 'Your pickup request has been saved.',
                status: 'success',
                duration: 7000,
              });
            }}
          />
        ) : null}
        <MaybeApiError error={createShipment.error} />
        {createShipment.isSuccess ? (
          <Button
            as={RouterLink}
            to="/admin/sample-shipping"
            alignSelf="flex-start"
            variant="outline"
            textTransform="none"
          >
            Back to shipments page
          </Button>
        ) : null}
      </VStack>
    </div>
  );
}

function getShippingWorkbookDisplayName(workbook: { name: string | null; sampleSheetName: string | null }): string {
  return workbook.name?.trim() || workbook.sampleSheetName?.trim() || 'Untitled workbook';
}
